// Cash Repairs: types and labels. Every figure and every refusal is Postgres
// (db/migrations/3103); nothing here decides who may do what or what anything
// costs. CLAUDE.md rule 4.
import type { ChipTone } from '../components/interviews/Chip'
import type { BorneBy, MaintenanceCategory } from './servicing'

export type CashRepairStatus = 'requested' | 'approved' | 'declined' | 'paid' | 'cancelled'

export const CASH_REPAIR_STATUS_LABEL: Record<CashRepairStatus, string> = {
  requested: 'Waiting for approval',
  approved: 'Approved, to pay',
  declined: 'Declined',
  paid: 'Paid',
  cancelled: 'Cancelled',
}

export const CASH_REPAIR_STATUS_TONE: Record<CashRepairStatus, ChipTone> = {
  requested: 'amber',
  approved: 'brand',
  declined: 'red',
  paid: 'green',
  cancelled: 'neutral',
}

export type CashRepairAccess = {
  linked?: boolean
  can_raise: boolean
  can_approve: boolean
  can_pay: boolean
  can_view: boolean
  approvers: string | null
  payers: string | null
}

export type CashRepairKind = { key: string; label: string; default_category: MaintenanceCategory }

export type CashRepairVehicle = {
  vehicle_id: string
  registration_number: string | null
  label: string
  drivers: { driver_id: string; driver_name: string | null; shifts: number }[]
}

export type CashRepairOptions = {
  access: CashRepairAccess
  kinds: CashRepairKind[]
  vehicles: CashRepairVehicle[]
  /** Who carries each category this month under the charge rule. */
  borne_by_category: Record<string, BorneBy | null>
}

export type CashRepairItem = {
  id: string
  position: number
  kind_key: string | null
  label: string
  description: string | null
  category: MaintenanceCategory
  quoted_zar: number
  paid_zar: number | null
  vat_zar: number
  line_id: string | null
  borne_by: BorneBy | null
}

export type CashRepair = {
  id: string
  ref: string
  status: CashRepairStatus
  reason: string
  vehicle_id: string
  registration_number: string | null
  driver_id: string | null
  driver_name: string | null
  requested_by: string
  requested_by_name: string | null
  requested_at: string
  approved_by_name: string | null
  approved_at: string | null
  declined_by_name: string | null
  declined_at: string | null
  decline_reason: string | null
  paid_by_name: string | null
  paid_at: string | null
  paid_note: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  is_mine: boolean
  /** cockpit: raised by staff before the spend. driver_shift_form: the driver paid and reported it (5703). */
  source?: 'cockpit' | 'driver_shift_form'
  paid_from?: 'own_cash' | 'uber_cash' | null
  /** The photo reader's check of the driver's slip, for driver_shift_form requests. */
  slip_check?: { verdict: string; message: string; read_zar: number | null } | null
  slip_confirmed_by_name?: string | null
  slip_confirmed_at?: string | null
  slip_confirmed_zar?: number | null
  /** What the driver typed, kept when a manager confirmed a different slip total (5704). */
  driver_typed_zar?: number | null
  quoted_zar: number
  paid_zar: number | null
  /** Quotes (source cash_quote) and paid slips (source cash_repair), oldest first. */
  receipts: {
    id: string
    filename: string | null
    read_status: string
    period_start: string
    source: 'cash_quote' | 'cash_repair' | 'upload'
    supplier_name: string | null
    total_zar: number | null
    read_error: string | null
  }[]
  items: CashRepairItem[]
}

export type CashRepairList = {
  access: CashRepairAccess
  counts: { requested: number; approved: number; paid: number; requested_zar: number; approved_zar: number }
  rows: CashRepair[]
}

/** Johannesburg wall clock, which is the only clock anyone at Fleetly reads. */
export function sast(ts: string | null | undefined): string {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/** What uploadCashRepairQuote returns: the quote as filed, and what the reader found on it. */
export type QuoteReading = {
  invoice_id: string
  attached_to: string | null
  supplier_name: string | null
  invoice_number: string | null
  total_zar: number | string | null
  read_status: 'pending' | 'read' | 'failed'
  read_error: string | null
  warnings: string[] | null
  items: {
    id: string
    position: number
    description: string
    quantity: number | string | null
    amount_ex_vat_zar: number | string | null
    vat_zar: number | string | null
    amount_incl_vat_zar: number | string | null
    suggested_category: string | null
    suggested_reason: string | null
    confidence: string | null
  }[]
}

/**
 * The cash a quoted item will cost: the VAT inclusive figure where the quote
 * prints one, else ex VAT plus VAT, else ex VAT alone. Null when the quote gave
 * no amount at all, so the form shows an empty box rather than R0.
 */
export function quotedCash(item: QuoteReading['items'][number]): number | null {
  const n = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v))
  const incl = n(item.amount_incl_vat_zar)
  if (incl !== null && Number.isFinite(incl)) return incl
  const ex = n(item.amount_ex_vat_zar)
  const vat = n(item.vat_zar)
  if (ex !== null && Number.isFinite(ex)) return vat !== null && Number.isFinite(vat) ? Math.round((ex + vat) * 100) / 100 : ex
  return null
}
